/* eslint-disable no-restricted-globals -- see the note on storage below. */
/**
 * Light and dark, and which one a visitor gets.
 *
 * Three states, not two. "system" is the default and means *follow the device*,
 * which is different from having picked light: a phone that flips to dark at
 * sunset should take the site with it unless the person has said otherwise.
 * Only an explicit choice is stored, so a visitor who has never touched the
 * control keeps following their device forever.
 *
 * The resolution itself lives in a tiny script in index.html that runs before
 * first paint — see THEME_BOOTSTRAP. Doing it in React would mean the page
 * renders light, then corrects itself, and a dark-mode user gets a white flash
 * on every single navigation to the site.
 */

export type ThemeChoice = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/**
 * The one thing this module persists, and why the lint rule is waived for it.
 *
 * `no-restricted-globals` bans localStorage across this codebase because an
 * access token in storage is an XSS away from being someone else's session.
 * That reasoning does not reach a theme preference: the value is the literal
 * string "light" or "dark", it is not a credential, it grants nothing, and
 * losing it costs a visitor one tap. It has to be storage rather than a
 * cookie or the server precisely because the pre-paint script needs it
 * synchronously, before React or any request exists.
 *
 * Nothing else in this file may be made to carry anything else.
 */
export const THEME_STORAGE_KEY = 'printoman:theme';

/**
 * The script that runs before anything is painted.
 *
 * Inlined into index.html as a blocking <script> in <head>. It is deliberately
 * tiny and dependency-free: everything it touches can throw (storage is
 * unavailable in some private modes, matchMedia is absent in very old
 * WebViews), so the whole thing is wrapped and failure simply leaves the
 * document on the light default rather than taking the page down.
 */
export const THEME_BOOTSTRAP = `(function () {
  try {
    var c = localStorage.getItem('${THEME_STORAGE_KEY}');
    if (c !== 'light' && c !== 'dark') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', c);
    }
  } catch (e) {}
})();`;

/**
 * Note what it does NOT do: look at `prefers-color-scheme`.
 *
 * It does not need to. Removing the attribute hands the question to the CSS
 * media query, which is already the authority on what "system" means — asking
 * matchMedia here as well would be a second implementation of the same rule,
 * free to disagree with the first.
 */

export function readStoredChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    // Private browsing, blocked site data, a sandboxed frame. Not an error.
    return 'system';
  }
}

export function systemPrefers(): ResolvedTheme {
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function resolve(choice: ThemeChoice): ResolvedTheme {
  return choice === 'system' ? systemPrefers() : choice;
}

/**
 * Put the choice on the document, where the CSS is listening.
 *
 * Removing the attribute rather than setting `data-theme="system"` is what
 * hands control back to the `prefers-color-scheme` media query — the CSS has
 * no "system" branch, and does not need one.
 */
export function applyChoice(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);

  try {
    if (choice === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // The theme still applies for this page; it just will not be remembered.
  }
}
